import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { type CollectionUserGroupData } from '@frontend/api';
import { useDisclosure } from '@frontend/components';
import { noop } from '@frontend/utils';

import { ConfirmChangeSiteDialog } from 'modules/quotes/quoteItems/modals/ConfirmChangeSiteDialog';

import { useAppSelector } from 'store/hooks';
import { getActiveGroup } from 'store/session/selectors';

import { useUserGroupLookup } from './hooks';

type NextSiteProps = {
  userGroupId: number;
  nextUrl: string;
  groupBusinessName: string;
};

type UserGroupCollectionContext = {
  userGroupsById: Record<number, CollectionUserGroupData>;
  getUserGroupById: (groupId: number) => CollectionUserGroupData | undefined;
  initChangeSite: (params: { nextUrl: string; userGroupId: number }) => void;
};

const UserGroupCollectionContext = createContext<UserGroupCollectionContext>({
  userGroupsById: {},
  getUserGroupById: () => undefined,
  initChangeSite: noop,
});

export const useUserGroupCollectionContext = () => {
  return useContext(UserGroupCollectionContext);
};

type UserGroupCollectionProviderProps = {
  children?: React.ReactNode;
};

export const UserGroupCollectionProvider = ({
  children,
}: UserGroupCollectionProviderProps) => {
  const confirmChangeSiteDialog = useDisclosure();

  const [nextSiteProps, setNextSiteProps] = useState<NextSiteProps | null>(
    null,
  );

  const { userGroupsById, getUserGroupById } = useUserGroupLookup();
  const activeGroup = useAppSelector(getActiveGroup);

  const initChangeSite = useCallback(
    ({ nextUrl, userGroupId }: { nextUrl: string; userGroupId: number }) => {
      if (userGroupId === activeGroup?.id) {
        window.location.href = nextUrl;
        return;
      }
      const group = getUserGroupById(userGroupId);
      setNextSiteProps({
        nextUrl,
        userGroupId,
        groupBusinessName: group?.businessName,
      });
      confirmChangeSiteDialog.onOpen();
    },
    [activeGroup?.id, confirmChangeSiteDialog, getUserGroupById],
  );

  const contextValue = useMemo(() => {
    return {
      userGroupsById,
      getUserGroupById,
      initChangeSite,
    };
  }, [userGroupsById, getUserGroupById, initChangeSite]);

  return (
    <UserGroupCollectionContext.Provider value={contextValue}>
      {children}
      {nextSiteProps && (
        <ConfirmChangeSiteDialog
          {...confirmChangeSiteDialog}
          {...nextSiteProps}
        />
      )}
    </UserGroupCollectionContext.Provider>
  );
};
